import React from "react";
import { Card, Overline, SegmentedControl, Select } from "../../../ForBrands/ui/BrandUI";
import { MobileCard } from "../../../ForBrands/ui/mobile/MobileUI";
import { usePhoneTheme } from "../../../ForBrands/useIsPhone";
import { SegmentedControl as PhoneSegmented } from "../../../auth/v2/pieces";
import { leagueLabelOf, teamNameOf } from "../../../ForCoaches/v2/coachUtils";
import { teamKeyOf } from "../allocationModel";

const PILL = "!h-10 !w-auto !rounded-full !pl-4 !pr-9 bm-label-l";

const VIEW_MODES = [
  { value: "coach", label: "Coach view" },
  { value: "league", label: "League admin view" },
];

const GENDERS = [
  ["all", "All genders"],
  ["M", "Boys / Men"],
  ["F", "Girls / Women"],
];

// A team by the one display rule (coachUtils.teamNameOf: "Oakland Soldiers
// 15U") and its league without the season or an age group the name says.
const nameOf = (t) => teamNameOf(t) || t?.teamId || "";
const leagueOf = (t, name) => leagueLabelOf(t?.cascade?.leagueDisplayName || t?.league || "", name);

// "Oakland Soldiers 15U · Nike EYBL (Boys) · HS / Grassroots (primary)".
const teamOptionLabel = (t) => {
  const name = nameOf(t);
  return `${[name, leagueOf(t, name), t.cascade?.tier || ""].filter(Boolean).join(" · ")}${t.isPrimary ? " (primary)" : ""}`;
};

const TeamSwitch = ({ teams, activeKey, onSwitch, className }) => (
  <Select value={activeKey} onChange={(e) => onSwitch(e.target.value)} disabled={teams.length < 2} className={className} aria-label="Switch team">
    {teams.map((t) => (
      <option key={teamKeyOf(t)} value={teamKeyOf(t)}>
        {teamOptionLabel(t)}
      </option>
    ))}
  </Select>
);

const GenderSelect = ({ gender, onGender, className }) => (
  <Select value={gender} onChange={(e) => onGender(e.target.value)} className={className} aria-label="Gender">
    {GENDERS.map(([value, label]) => (
      <option key={value} value={value}>
        {label}
      </option>
    ))}
  </Select>
);

// League-admin accounts switch between their own teams and every team in
// the leagues they run, optionally by gender.
const LeagueAdminControls = ({ viewMode, onViewMode, gender, onGender, showViewSwitch }) => (
  <>
    {showViewSwitch && <SegmentedControl value={viewMode} onChange={onViewMode} options={VIEW_MODES} />}
    {viewMode === "league" && <GenderSelect gender={gender} onGender={onGender} className={PILL} />}
  </>
);

// The same controls on phones, full width (D-cs8, not drawn): the mobile
// segmented, then a 56px gender select.
const PhoneLeagueAdminControls = ({ viewMode, onViewMode, gender, onGender, showViewSwitch }) => (
  <>
    {showViewSwitch && <PhoneSegmented label="View" value={viewMode} onChange={onViewMode} options={VIEW_MODES.map((m) => [m.value, m.label])} />}
    {viewMode === "league" && <GenderSelect gender={gender} onGender={onGender} className="bm-input-lg" />}
  </>
);

// Figma 03b · Your team, phone form (2415:1544): the name with league, tier
// and body on one line under it, the 56px team switch, then any
// league-admin controls stacked under it.
const PhoneTeamCard = ({ record, teams, activeKey, onSwitch, leagueAdmin }) => {
  const cascade = record?.cascade || {};
  const title = record ? teamNameOf(record) || "Your team" : "No teams for this filter";
  const line = [record ? leagueOf(record, title) : "", cascade.tier, cascade.body].filter(Boolean).join(" · ");
  return (
    <MobileCard className="!gap-[10px]">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-[2px]">
        <Overline className="!text-bm-tertiary">Your team</Overline>
        <span className="bm-body-s text-bm-tertiary">Locked from your coach profile</span>
      </div>
      <div className="flex flex-col gap-[2px] break-words">
        <span className="bm-h-m text-bm-ink">{title}</span>
        {line && <span className="bm-body-s text-bm-muted">{line}</span>}
      </div>
      {teams.length > 0 && <TeamSwitch teams={teams} activeKey={activeKey} onSwitch={onSwitch} className="bm-input-lg" />}
      {leagueAdmin && <PhoneLeagueAdminControls {...leagueAdmin} />}
      {leagueAdmin?.viewMode === "league" && <span className="bm-body-s text-bm-tertiary">{teams.length} teams</span>}
    </MobileCard>
  );
};

// Figma 03b · Your team: the coach's locked team with the team switch. A
// league admin whose gender filter matches no team keeps the card (and the
// filter) with no record.
const TeamCard = (props) => {
  const phone = usePhoneTheme();
  if (phone) return <PhoneTeamCard {...props} />;
  const { record, teams, activeKey, onSwitch, leagueAdmin } = props;
  const cascade = record?.cascade || {};
  const title = record ? teamNameOf(record) || "Your team" : "No teams for this filter";
  const league = record ? leagueOf(record, title) : "";
  const context = [cascade.tier, cascade.body].filter(Boolean).join(" · ");
  return (
    <Card padding="px-5 py-4" className="flex flex-wrap items-center justify-between gap-6">
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex items-center gap-3">
          <Overline className="!text-bm-tertiary">Your team</Overline>
          <span className="bm-body-s text-bm-tertiary">Locked from your coach profile</span>
        </div>
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="bm-h-m text-bm-ink">{title}</span>
          {league && <span className="bm-body-m text-bm-muted">{league}</span>}
          {context && (
            <>
              <span className="bm-body-m text-bm-tertiary">·</span>
              <span className="bm-body-m text-bm-tertiary">{context}</span>
            </>
          )}
        </div>
      </div>
      <div className="flex min-w-0 flex-wrap items-center gap-3">
        {leagueAdmin && <LeagueAdminControls {...leagueAdmin} />}
        {teams.length > 0 && <TeamSwitch teams={teams} activeKey={activeKey} onSwitch={onSwitch} className="!w-[480px] max-w-full" />}
        {leagueAdmin?.viewMode === "league" && <span className="bm-body-s text-bm-tertiary">{teams.length} teams</span>}
      </div>
    </Card>
  );
};

export default TeamCard;
